import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { useGetZendeskChatWidgetsQuery } from 'src/services';
import { useParams } from 'react-router-dom';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { WebWidgetSelect } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskChatIntegrationSettings/EditZendeskChatWidgetSettings/WebWidgetSelect';
import { Switch } from 'src/components/ui/switch';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useIsCosupportManager } from 'src/hooks/useIsCosupportManager';

export interface ZendeskChatAutoSetupData {
  integrationId: number | string;
  widget_integration_id: string;
  responder_mode: 'handoff' | 'default_responder';
}

export const IntegrationZendeskChatAutoSetup = ({
  integration,
  onSubmit,
}: {
  // A project integration or the one being set up in the integrations modal.
  integration: { id?: number | string };
  onSubmit: (data: ZendeskChatAutoSetupData) => void;
}) => {
  const { id } = useParams();
  const isCosupportManager = useIsCosupportManager();
  const { data: widgets, isFetching: widgetsIsFetching } = useGetZendeskChatWidgetsQuery({
    projectId: id,
    integrationId: integration.id,
  });

  useLoaderWhile(widgetsIsFetching);

  const { handleSubmit, control } = useForm({
    defaultValues: {
      widget_integration_id: '',
      responder_mode: 'default_responder',
      flow_builder: false,
    },
    resolver: yupResolver(
      object({
        widget_integration_id: stringScheme({ required: false }).label('Widget integration id'),
        responder_mode: stringScheme({ required: true }).label('Responder Mode'),
        flow_builder: booleanScheme({ required: true }),
      }),
    ),
  });

  const submit = (data: {
    widget_integration_id: string;
    responder_mode: string;
    flow_builder: boolean;
  }) => {
    onSubmit({
      integrationId: integration.id,
      widget_integration_id: data.widget_integration_id,
      responder_mode: data.flow_builder ? 'handoff' : 'default_responder',
    });
  };

  return (
    <form id="auto-setup-form" onSubmit={handleSubmit(submit)} className={'min-h-[40vh]'}>
      <div className="content">
        <LoaderWrapper loader={<Skeleton className="w-full h-16" />} isLoading={widgetsIsFetching}>
          {widgets?.length ? (
            <>
              <WebWidgetSelect
                label={<div className={'flex flex-row gap-2 items-center mb-1'}>Web Widget</div>}
                control={control}
                name={'widget_integration_id'}
                options={widgets || []}
              />
              {isCosupportManager ? (
                <div className="flex items-center gap-2.5 mt-5">
                  <p className="font-semibold text-sm text-ebony opacity-80">Chat Flow Builder</p>

                  <Controller
                    control={control}
                    name={'flow_builder'}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Switch defaultChecked={value} checked={value} onCheckedChange={onChange} />
                    )}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <p>Please make sure your integration details are filled correctly</p>
          )}
        </LoaderWrapper>
      </div>
    </form>
  );
};
